import { useSuspenseLiveQuery } from "@/hooks/useSuspenseLiveQuery";
import { MessageDestinationType } from "@/moby/db";
import { mui } from "@/mui";
import { EditIcon, SearchIcon, LogOut, Trash2Icon, PhoneIcon, PhoneOff, XCircle, MailIcon, RefreshCwIcon, SettingsIcon, Fullscreen, Settings, PenSquare, CrownIcon, Settings2, UserPlusIcon, UsersIcon, PlusIcon, MessageSquareIcon } from "lucide-react";
import { useCallback, useEffect, useRef, useState, type ComponentProps, type ReactNode } from "react";
import { Link, useLocation, useParams } from "wouter";
import { useMobyDB, useMobyNodeClient, useSession } from "../provider";
import { getEmailAccountsFromDB, refreshOAuth2TokenIfNeeded } from "@/services/email-account-store";
import { UserPreferencesDialog } from "../user-preferences-dialog";
import { EmailSettingsDialog, getEmailSettings } from "../email-settings-dialog";
import { useMediaSessions } from "@/hooks/useMediaSessions";
import { useUserPreferences } from "@/hooks/useUserPreferences";
import { getPeerAvatarColor, renderPeerAvatarEmoji } from "@/lib/peer-profile-utils";
import { PeerHealthDot } from "../peer-health-dot";
import { StreamWindow } from "../video/stream-window";
import { AVSettingsDialog } from "./av-settings-dialog";
import { NewGroupChatDialog } from "./new-group-chat-dialog";
import type { EmailAccount } from "../email-drawer";
import { getProviderLogo } from "../email-provider-logos";
import { useLayoutPreferences, SPLIT_PRESETS } from "@/hooks/useLayoutPreferences";
import { EditContactDialog } from "../edit-contact-dialog";
import { emailWorkerService, type EmailAccountForWorker } from "@/services/email-worker-service";

interface EmailMessage {
  id: string;
  uid: number;
  accountId: string;
  accountColor: string;
  from: string;
  subject: string;
  date: Date;
  seen: boolean;
  hasAttachment?: boolean;
}


// Selected email for viewing in the main panel
export interface SelectedEmail extends EmailMessage {
  account: EmailAccount;
}

// Legacy localStorage fallback — kept for SelectedEmail.account hydration in non-DB contexts
function getEmailAccountsFromLocalStorage(): EmailAccount[] {
  try {
    const stored = localStorage.getItem("moby_pref_emailAccounts");
    if (stored) {
      const accounts = JSON.parse(stored) as EmailAccount[];
      return accounts.map(account => ({
        ...account,
        authType: account.authType || "password",
      }));
    }
  } catch { /* ignore */ }
  return [];
}

interface EmailListPanelProps {
  onSelectEmail?: (email: SelectedEmail | null) => void;
}

function EmailListPanel({ onSelectEmail }: EmailListPanelProps) {
  const db = useMobyDB();
  const [accounts, setAccounts] = useState<EmailAccount[]>([]);
  const [emails, setEmails] = useState<EmailMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [accountErrors, setAccountErrors] = useState<Map<string, string>>(new Map());
  const [accountCounts, setAccountCounts] = useState<Map<string, number>>(new Map());

  const loadAccounts = useCallback(async () => {
    const loaded = await getEmailAccountsFromDB(db);
    setAccounts(loaded);
  }, [db]);

  useEffect(() => {
    void loadAccounts();
    const handlePrefsUpdate = () => { void loadAccounts(); };
    window.addEventListener("user-preferences-updated", handlePrefsUpdate);
    // Optimistic delete: remove email from local list when email-deleted event fires
    const handleEmailDeleted = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.id) {
        setEmails(prev => prev.filter(em => em.id !== detail.id));
      }
    };
    window.addEventListener("email-deleted", handleEmailDeleted);
    return () => {
      window.removeEventListener("user-preferences-updated", handlePrefsUpdate);
      window.removeEventListener("email-deleted", handleEmailDeleted);
    };
  }, [loadAccounts]);

  // Initialize email worker on mount (fire-and-forget, fallback is automatic)
  useEffect(() => {
    emailWorkerService.init().catch(() => { /* service logs internally */ });
  }, []);

  const fetchEmails = useCallback(async () => {
    const gatewayUrl = localStorage.getItem("moby_pref_emailGatewayUrl")?.trim();
    const enabledAccounts = accounts.filter(a => a.enabled);
    const settings = getEmailSettings();
    
    console.log("[EmailListPanel] fetchEmails called", { gatewayUrl, enabledCount: enabledAccounts.length, settings, workerActive: emailWorkerService.isWorkerActive });
    
    if (!gatewayUrl || enabledAccounts.length === 0) {
      console.log("[EmailListPanel] Skipping - no gateway URL or no enabled accounts");
      setEmails([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Refresh expired OAuth2 tokens before polling IMAP
      const refreshedAccounts = await Promise.all(
        enabledAccounts.map(async (a) => {
          if (a.authType !== 'oauth2') return a;
          const result = await refreshOAuth2TokenIfNeeded(db, a, gatewayUrl);
          if (result.status === 'refreshed') {
            return { ...a, accessToken: result.accessToken, tokenExpiry: result.tokenExpiry };
          }
          if (result.status === 'revoked') {
            console.warn(`[EmailListPanel] OAuth REVOKED for ${a.email}: ${result.error}`);
            window.dispatchEvent(new CustomEvent('email-auth-error', {
              detail: { accountId: a.id, email: a.email, error: result.error, revoked: true },
            }));
          }
          return a;
        })
      );

      // Route IMAP polling through email worker (off main thread) when available,
      // falls back to main-thread fetch automatically
      const workerAccounts: EmailAccountForWorker[] = refreshedAccounts.map(a => ({
        id: a.id,
        email: a.email,
        imapHost: a.imapHost,
        imapPort: a.imapPort,
        authType: a.authType,
        username: a.username,
        password: a.password,
        accessToken: a.accessToken,
        color: a.color,
        enabled: a.enabled,
      }));

      const result = await emailWorkerService.fetchEmails(gatewayUrl, workerAccounts, settings.messagesPerAccount);

      // Convert ISO date strings back to Date objects and apply sort
      const allEmails: EmailMessage[] = result.emails.map(e => ({
        ...e,
        date: new Date(e.date),
      }));

      if (settings.sortOrder === "newest") {
        allEmails.sort((a, b) => b.date.getTime() - a.date.getTime());
      } else {
        allEmails.sort((a, b) => a.date.getTime() - b.date.getTime());
      }

      setEmails(allEmails);
      setAccountErrors(new Map(Object.entries(result.errors)));
      setAccountCounts(new Map(Object.entries(result.counts).map(([k, v]) => [k, Number(v)])));

      console.log(`[EmailListPanel] Fetch complete: ${allEmails.length} total emails from ${enabledAccounts.length} accounts`, {
        perAccount: result.counts,
        errors: result.errors
      });
    } catch (err) {
      console.error("[EmailListPanel] fetchEmails error:", err);
      setError(err instanceof Error ? err.message : "Fetch failed");
    } finally {
      setLoading(false);
    }
  }, [accounts]);

  useEffect(() => {
    if (accounts.length > 0) {
      fetchEmails();
    }
  }, [accounts, fetchEmails]);

  // Listen for email settings changes to refresh
  useEffect(() => {
    const handleSettingsUpdate = () => {
      console.log("[EmailListPanel] Settings updated, refreshing...");
      fetchEmails();
    };
    window.addEventListener("email-settings-updated", handleSettingsUpdate);
    return () => window.removeEventListener("email-settings-updated", handleSettingsUpdate);
  }, [fetchEmails]);

  const formatDate = (date: Date) => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return date.toLocaleDateString([], { weekday: 'short' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };

  if (accounts.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center text-on-surface-variant px-4">
        <MailIcon size={48} className="mb-4 opacity-38" />
        <p className="text-body-lg mb-2">No email accounts</p>
        <p className="text-body-sm mb-4">Add an account to see your emails here</p>
        <mui.Button
          variant="primary"
          filled
          onClick={() => window.dispatchEvent(new Event("open-email-drawer"))}
        >
          <SettingsIcon size={16} className="mr-2" />
          Configure Email
        </mui.Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between p-3 border-b border-outline-variant">
        <div className="flex items-center gap-2">
          <span className="text-label-sm text-on-surface-variant">
            {emails.length} message{emails.length !== 1 ? 's' : ''}
          </span>
          {/* Per-account status dots */}
          <div className="flex items-center gap-1">
            {accounts.filter(a => a.enabled).map(account => {
              const count = accountCounts.get(account.id);
              const hasError = accountErrors.has(account.id);
              return (
                <div
                  key={account.id}
                  className={`w-2 h-2 rounded-full ${hasError ? 'bg-error' : count !== undefined && count > 0 ? 'bg-primary' : 'bg-outline'}`}
                  title={`${account.email}: ${hasError ? accountErrors.get(account.id) : count !== undefined ? `${count} messages` : 'Not fetched'}`}
                />
              );
            })}
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => window.dispatchEvent(new CustomEvent("email-compose-new"))}
            className="p-2 rounded-full hover:bg-on-surface/8 text-on-surface-variant"
            title="Compose new email"
          >
            <PenSquare size={16} />
          </button>
          <button
            onClick={fetchEmails}
            disabled={loading}
            className="p-2 rounded-full hover:bg-on-surface/8 text-on-surface-variant"
            title="Refresh emails"
          >
            <RefreshCwIcon size={16} className={loading ? "animate-spin" : ""} />
          </button>
          <EmailSettingsDialog>
            <mui.Dialog.Trigger
              className="p-2 rounded-full hover:bg-on-surface/8 text-on-surface-variant"
              title="Email settings"
            >
              <SettingsIcon size={16} />
            </mui.Dialog.Trigger>
          </EmailSettingsDialog>
        </div>
      </div>

      {/* Show per-account errors — classify auth errors with re-auth action */}
      {accountErrors.size > 0 && (
        <div className="px-3 py-2 bg-error/10 text-error text-body-sm space-y-1">
          {Array.from(accountErrors.entries()).map(([accountId, errMsg]) => {
            const account = accounts.find(a => a.id === accountId);
            const isAuthError = /login failed|invalid credentials|authentication|auth.*fail|xoauth2|invalid_grant/i.test(errMsg);
            return (
              <div key={accountId} className="flex items-center justify-between gap-2">
                <span>
                  <strong>{account?.email || 'Unknown'}:</strong>{' '}
                  {isAuthError ? 'Authentication failed — token may be expired or revoked' : errMsg}
                </span>
                {isAuthError && (
                  <button
                    onClick={() => window.dispatchEvent(new CustomEvent('open-email-drawer', { detail: { editAccountId: accountId } }))}
                    className="flex-shrink-0 text-label-sm px-2 py-0.5 rounded bg-error/20 hover:bg-error/30 text-error whitespace-nowrap"
                  >
                    Re-authenticate
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {error && (
        <div className="px-3 py-2 bg-error/10 text-error text-body-sm">
          {error}
        </div>
      )}

      {emails.length === 0 && !loading ? (
        <div className="flex-1 flex items-center justify-center text-on-surface-variant text-body-sm">
          No emails found. Click refresh to check.
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto">
          {emails.map((email) => {
            const account = accounts.find(a => a.id === email.accountId);
            return (
              <div
                key={email.id}
                className={`flex items-stretch border-b border-outline-variant hover:bg-on-surface/4 cursor-pointer ${
                  !email.seen ? "bg-primary/5" : ""
                }`}
                onClick={() => {
                  if (onSelectEmail && account) {
                    onSelectEmail({ ...email, account });
                  }
                }}
              >
                {/* Provider SVG logo — color column, edge-to-edge */}
                <div
                  className="w-10 flex-shrink-0 flex items-center justify-center"
                  style={{ backgroundColor: email.accountColor }}
                  title={account?.email}
                >
                  {getProviderLogo(account?.email || email.from).Logo({ size: 22 })}
                </div>
                <div className="flex-1 min-w-0 px-3 py-2.5">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className={`text-body-sm truncate ${!email.seen ? "font-medium text-on-surface" : "text-on-surface-variant"}`}>
                      {email.from}
                    </span>
                    <span className="text-label-sm text-on-surface-variant flex-shrink-0">
                      {formatDate(email.date)}
                    </span>
                  </div>
                  <div className={`text-body-sm truncate flex items-center gap-1 ${!email.seen ? "text-on-surface" : "text-on-surface-variant"}`}>
                    {email.hasAttachment && <span className="flex-shrink-0">📎</span>}
                    <span className="truncate">{email.subject || "(no subject)"}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function ChatListPanel() {
  const db = useMobyDB();
  const node = useMobyNodeClient();
  const { chatAddress } = useParams<"/chats/:chatAddress">();
  const [location, navigate] = useLocation();
  const userPreferences = useUserPreferences();
  const { isCompact, splitRatio, setSplitRatio } = useLayoutPreferences();
  
  // =====================================================================
  // UI REACTIVITY FIX - Force refresh when conversations are deleted remotely
  // =====================================================================
  // Track deletion events to force useSuspenseLiveQuery to refresh
  const [deletionTrigger, setDeletionTrigger] = useState(0);
  
  // Track active tab - automatically switch to groups when viewing a group chat
  const [activeTab, setActiveTab] = useState<string>("conversations");
  
  // New Group dialog state (triggered from empty groups tab)
  const [showNewGroupDialog, setShowNewGroupDialog] = useState(false);
  
  // Listen for external open requests for the new group dialog
  useEffect(() => {
    const open = () => setShowNewGroupDialog(true);
    window.addEventListener("open-new-group-dialog", open);
    return () => window.removeEventListener("open-new-group-dialog", open);
  }, []);

  const session = useSession();
  
  // Get active media sessions
  const mediaSessions = useMediaSessions();

  const conversations = useSuspenseLiveQuery(async () => {
    const chats = await db.getLatestChats({
      type: MessageDestinationType.DM,
    });

    return Promise.all(
      chats.map(async (chat) => {
        const [contact, lastMessage] = await Promise.all([
          db.contacts.get({ address: chat.peers[0] }),
          chat.getLastMessage(),
        ]);

        if (contact === undefined) {
          throw new Error(
            `Contact not found for chat ${chat.address} (peer ${chat.peers[0]}).`,
          );
        }

        return {
          chat,
          contact,
          lastMessage,
          // Include connection health metrics for UI indicators
          connectionHealth: contact.connection,
          // Include remote peer profile for avatar/emoji rendering
          profile: contact.custom?.profile,
        };
      }),
    );
  }, [deletionTrigger]); // Include deletionTrigger to force refresh

  const groups = useSuspenseLiveQuery(async () => {
    const chats = await db.getLatestChats({
      type: MessageDestinationType.GROUP,
    });

    return Promise.all(
      chats.map(async (chat) => ({
        chat,
        lastMessage: await chat.getLastMessage(),
      })),
    );
  }, [deletionTrigger]); // Include deletionTrigger to force refresh

  useEffect(() => {
    let unsubscribe: (() => void) | undefined;
    
    // Handle async subscription for conversations
    node.subscribeChatsToSync(
      conversations.map((row) => row.chat.address),
    ).then((unsub) => {
      unsubscribe = unsub;
    }).catch((error) => {
      console.error('Failed to subscribe conversations to sync:', error);
    });

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [conversations]);

  useEffect(() => {
    let unsubscribe: (() => void) | undefined;
    
    // EVENT-DRIVEN GROUP SYNC: Register groups for reconnect/event sync only (no polling)
    node.subscribeGroupsToEventSync(
      groups.map((row) => row.chat.address),
    ).then((unsub) => {
      unsubscribe = unsub;
    }).catch((error) => {
      console.error('Failed to register groups for event sync:', error);
    });

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [groups]);
  
  // =====================================================================
  // AUTO TAB SWITCHING - Switch to groups tab when viewing a group chat
  // CRITICAL: Only switch when chatAddress CHANGES, not on peer updates
  // This prevents blocking manual tab switches during reconnections
  // =====================================================================
  const prevChatAddressRef = useRef<string | undefined>(undefined);
  
  useEffect(() => {
    if (!chatAddress) {
      prevChatAddressRef.current = undefined;
      return;
    }
    
    // Only auto-switch if chatAddress actually changed (user navigated to different chat)
    // Don't switch on peer reconnections or manual tab changes
    if (prevChatAddressRef.current === chatAddress) {
      return; // chatAddress hasn't changed, don't override user's tab choice
    }
    
    prevChatAddressRef.current = chatAddress;
    
    // Check if the current chat is a group
    const isGroup = groups.some(row => row.chat.address === chatAddress);
    
    if (isGroup && activeTab !== "groups") {
      console.log('[TAB-SWITCH] Auto-switching to groups tab (chat navigation)', {
        chatAddress: chatAddress.slice(0, 16) + '...',
        previousTab: activeTab
      });
      setActiveTab("groups");
    } else if (!isGroup) {
      const isConversation = conversations.some(row => row.chat.address === chatAddress);
      if (isConversation && activeTab !== "conversations") {
        console.log('[TAB-SWITCH] Auto-switching to conversations tab (chat navigation)', {
          chatAddress: chatAddress.slice(0, 16) + '...',
          previousTab: activeTab
        });
        setActiveTab("conversations");
      }
    }
  }, [chatAddress, groups, conversations, activeTab]);
  
  // =====================================================================
  // REMOTE DELETION EVENT HANDLER - Listen for synchronized deletions
  // =====================================================================
  useEffect(() => {
    const handleConversationDeleted = async (event: Event) => {
      const customEvent = event as CustomEvent;
      const { conversationAddress, deletionCounter, tombstoneVerified } = customEvent.detail;
      
      console.log("[CHAT-LIST-REFRESH]  Received moby-conversation-deleted event", {
        conversationAddress: conversationAddress?.slice(0, 16) + "...",
        deletionCounter,
        tombstoneVerified,
        currentTrigger: deletionTrigger,
        timestamp: Date.now()
      });

      // DEBUGGING: Check if conversation is actually tombstoned in the database
      try {
        const isTombstoned = await db.isConversationTombstoned(conversationAddress);
        console.log("[CHAT-LIST-REFRESH] 🔍 Tombstone verification check", {
          conversationAddress: conversationAddress?.slice(0, 16) + "...",
          isTombstoned,
          timestamp: Date.now()
        });
      } catch (err) {
        console.error("[CHAT-LIST-REFRESH] Failed to check tombstone status", err);
      }
      
      // Force refresh by incrementing the deletion trigger
      const oldTrigger = deletionTrigger;
      setDeletionTrigger(prev => {
        const newTrigger = prev + 1;
        console.log("[CHAT-LIST-REFRESH]  Updated deletion trigger", {
          oldTrigger,
          newTrigger,
          conversationAddress: conversationAddress?.slice(0, 16) + "...",
          timestamp: Date.now()
        });
        return newTrigger;
      });
      
      // Navigate away if currently viewing the deleted conversation
      if (chatAddress && chatAddress === conversationAddress) {
        console.log("[CHAT-LIST-REFRESH]  Navigating away from deleted conversation", {
          deletedChatAddress: conversationAddress?.slice(0, 16) + "...",
          currentPath: window.location.pathname,
          timestamp: Date.now()
        });
        navigate('/chats');
      }
    };
    
    window.addEventListener('moby-conversation-deleted', handleConversationDeleted);
    
    return () => {
      window.removeEventListener('moby-conversation-deleted', handleConversationDeleted);
    };
  }, [chatAddress, navigate, deletionTrigger]);
  
  // =====================================================================
  // NAVIGATE-TO-CHAT - Listen for programmatic navigation requests
  // (e.g. after auto-joining a group from an email invite)
  // =====================================================================
  useEffect(() => {
    const handleNavigateToChat = (e: Event) => {
      const addr = (e as CustomEvent).detail?.chatAddress;
      if (addr && typeof addr === 'string') {
        console.log('[NAVIGATE] Programmatic navigation to chat', { chatAddress: addr.slice(0, 16) + '...' });
        navigate(`/chats/${addr}`);
      }
    };
    
    window.addEventListener('navigate-to-chat', handleNavigateToChat);
    return () => window.removeEventListener('navigate-to-chat', handleNavigateToChat);
  }, [navigate]);
  
  // =====================================================================
  // STREAM TAB SWITCH - Listen for active call notifications
  // =====================================================================
  useEffect(() => {
    const handleSwitchToStreamsTab = () => {
      console.log('[TAB-SWITCH] Switching to streams tab (active call detected)');
      setActiveTab('streams');
    };
    
    window.addEventListener('switch-to-streams-tab', handleSwitchToStreamsTab);
    
    return () => {
      window.removeEventListener('switch-to-streams-tab', handleSwitchToStreamsTab);
    };
  }, []);

  if (session === undefined) {
    return null;
  }

  // In compact mode: navigate to chat also auto-switches split to show panel
  const navigateToChat = useCallback((address: string) => {
    if (isCompact && splitRatio > SPLIT_PRESETS.middle + 5) {
      setSplitRatio('middle');
    }
    navigate(`/chats/${address}`);
  }, [isCompact, splitRatio, setSplitRatio, navigate]);

  // Compact mode: height-based sizing with splitRatio, always visible
  // Desktop mode: width-based sizing, hidden when chatAddress is set on mobile
  const containerClasses = isCompact
    ? 'flex w-full flex-col shrink-0 overflow-hidden'
    : `flex w-full flex-col sm:mr-6 sm:flex sm:w-96 sm:shrink-0 ${chatAddress !== undefined ? "hidden sm:flex" : ""}`;

  const containerStyle = isCompact
    ? { height: `${splitRatio}%` }
    : undefined;

  return (
    <>
    <div
      className={containerClasses}
      style={containerStyle}
    >
      <div
        className={`text-on-surface rounded-t-large sm:rounded-tr-large rounded-tr-none ${isCompact ? 'p-2' : 'p-4'}`}
        style={{ backgroundColor: userPreferences.bannerColor }}
      >
        <div className="flex items-center justify-between">
          <div className={`flex items-center ${isCompact ? 'gap-2' : 'gap-0'}`}>
            <div className="relative">
              <div 
                className={`grid ${isCompact ? 'size-7' : 'size-10'} place-items-center rounded-full text-white`}
                style={{ backgroundColor: userPreferences.avatarColor }}
              >
                <div className={isCompact ? "text-[10px] font-medium" : "text-label-md"}>{session.name.slice(0, 2)}</div>
                {renderPeerAvatarEmoji({ avatarEmoji: userPreferences.avatarEmoji } as any, "sm")}
              </div>
              {userPreferences.presence === "online" && (
                <div className="absolute -bottom-1 -right-1 bg-green-500 rounded-full w-3 h-3 border-2 border-surface-container-high" />
              )}
              {userPreferences.presence === "dnd" && (
                <div className="absolute -bottom-1 -right-1 bg-black rounded-full w-3 h-3 border-2 border-surface-container-high flex items-center justify-center">
                  <XCircle size={8} className="text-white" strokeWidth={3} />
                </div>
              )}
            </div>
            {isCompact && (
              <span className="text-label-md text-on-surface truncate max-w-[120px]">{session.name}</span>
            )}
          </div>

          <div className="flex gap-1">
            <UserPreferencesDialog>
              <mui.Dialog.Trigger
                className={`group text-on-surface-variant relative rounded-full p-2 transition duration-150 hover:cursor-pointer`}
              >
                <SettingsIcon size={isCompact ? 16 : 20} />
                <span
                  aria-hidden
                  className="group-hover:bg-on-surface-variant/8 group-active:bg-on-surface-variant/10 absolute inset-0 rounded-full transition"
                />
              </mui.Dialog.Trigger>
            </UserPreferencesDialog>

            {!isCompact && (
              <button
                disabled
                title="Search"
                aria-label="Search"
                className={`group text-on-surface-variant relative rounded-full p-2 transition duration-150 hover:cursor-pointer disabled:pointer-events-none disabled:opacity-38`}
              >
                <SearchIcon size={20} />
                <span
                  aria-hidden
                  className="group-hover:bg-on-surface-variant/8 group-active:bg-on-surface-variant/10 absolute inset-0 rounded-full transition"
                />
              </button>
            )}
          </div>
        </div>

        {!isCompact && (
          <h2 className="text-title-lg text-primary mt-4 line-clamp-1">
            {session.name}'s chats
          </h2>
        )}
      </div>

      <mui.Tabs.Root
        value={activeTab}
        onValueChange={(value) => {
          setActiveTab(value);
          // Clear email view/compose state when switching away from email tab
          if (value !== "email") {
            window.dispatchEvent(new CustomEvent("email-panel-close"));
          }
          // PANEL FIX 2: Clear AV stream panel when switching away from streams tab.
          // Without this, showStreamPanel stays true and blocks ChatPanel rendering.
          if (value !== "streams") {
            window.dispatchEvent(new CustomEvent("av-stream-panel-close"));
          }
          // Clear calendar panel when switching tabs
          window.dispatchEvent(new CustomEvent("calendar-panel-close"));
        }}
        className="bg-surface flex min-w-0 flex-1 flex-col"
      >
        {/* Material Tabs */}
        <mui.Tabs.List>
          <mui.Tabs.Tab value="conversations">Chats</mui.Tabs.Tab>
          <mui.Tabs.Tab value="groups">Groups</mui.Tabs.Tab>
          <mui.Tabs.Tab value="streams">Streams</mui.Tabs.Tab>
          <mui.Tabs.Tab value="email">Email</mui.Tabs.Tab>
          <mui.Tabs.Indicator />
        </mui.Tabs.List>
        <mui.Tabs.Panel
          value="conversations"
          className="flex-1 basis-0 overflow-y-auto"
        >
          {conversations.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center text-on-surface-variant px-4">
              <MessageSquareIcon size={48} className="mb-4 opacity-38" />
              <p className="text-body-lg mb-2">No conversations yet</p>
              <p className="text-body-sm mb-4">Add a contact to start chatting</p>
              <mui.Button
                variant="primary"
                filled
                onClick={() => window.dispatchEvent(new Event("open-contacts-drawer"))}
              >
                <UserPlusIcon size={16} className="mr-2" />
                Add Contact
              </mui.Button>
            </div>
          ) : (
            conversations.map((row) => (
              <ChatListItem
                key={row.chat.address}
                aria-selected={row.chat.address === chatAddress}
                onClick={(e) => {
                  e.preventDefault();
                  // Clear overlay panels when selecting a conversation
                  window.dispatchEvent(new CustomEvent("email-panel-close"));
                  window.dispatchEvent(new CustomEvent("av-stream-panel-close"));
                  window.dispatchEvent(new CustomEvent("calendar-panel-close"));
                  navigateToChat(row.chat.address);
                }}
                chat={{
                  address: row.chat.address,
                  name: row.contact.getDisplayName(),
                  initials: row.contact.getInitials(),
                  lastMessage: row.lastMessage?.text ?? (
                    <span className="text-">No messages yet</span>
                  ),
                  lastActive: row.lastMessage?.timestamp.toLocaleString(),
                  profile: row.profile, // Pass remote peer profile for avatar/emoji
                }}
                connectionHealth={row.connectionHealth}
                badgeCount={row.chat.getTotalBadgeCount()}
              />
            ))
          )}
        </mui.Tabs.Panel>

        <mui.Tabs.Panel
          value="groups"
          className="flex-1 basis-0 overflow-y-auto"
        >
          {groups.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center text-on-surface-variant px-4">
              <UsersIcon size={48} className="mb-4 opacity-38" />
              <p className="text-body-lg mb-2">No group chats yet</p>
              <p className="text-body-sm mb-4">Create a group to start collaborating</p>
              <mui.Button
                variant="primary"
                filled
                onClick={() => window.dispatchEvent(new Event("open-new-group-dialog"))}
              >
                <PlusIcon size={16} className="mr-2" />
                Create Group
              </mui.Button>
            </div>
          ) : (
            groups.map((row) => (
              <ChatListItem
                key={row.chat.address}
                aria-selected={row.chat.address === chatAddress}
                onClick={(e) => {
                  e.preventDefault();
                  // PANEL FIX 2: Clear overlay panels when selecting a group
                  window.dispatchEvent(new CustomEvent("av-stream-panel-close"));
                  window.dispatchEvent(new CustomEvent("calendar-panel-close"));
                  navigateToChat(row.chat.address);
                }}
                chat={{
                  address: row.chat.address,
                  name: row.chat.name ?? "Group",
                  initials: row.chat.name?.slice(0, 2) ?? "Gr",
                  lastMessage: row.lastMessage?.text ?? (
                    <span className="text-">No messages yet</span>
                  ),
                  lastActive: row.lastMessage?.timestamp.toLocaleString(),
                }}
                // For groups, we don't show individual peer status
                connectionHealth={undefined}
                badgeCount={row.chat.getTotalBadgeCount()}
              />
            ))
          )}
        </mui.Tabs.Panel>

        <mui.Tabs.Panel
          value="streams"
          className="flex-1 basis-0 overflow-y-auto p-4"
        >
          {/* CRITICAL: Only show ACTIVE streams in this tab, not ringing/offering/connecting */}
          {/* Those states are handled in the A/V Stream drawer for call setup */}
          {(() => {
            const activeSessions = mediaSessions.filter(s => s.session.state === 'active');
            
            if (activeSessions.length === 0) {
              return (
                <div className="flex flex-col items-center justify-center h-full text-center text-on-surface-variant px-4">
                  <PhoneIcon size={48} className="mb-4 opacity-38" />
                  <p className="text-body-lg mb-2">No active streams</p>
                  <p className="text-body-sm">
                    Start an audio call from A/V Stream to see it here
                  </p>
                </div>
              );
            }
            
            // Group sessions by sessionId for group calls
            // Group calls share the same sessionId across multiple peers
            const sessionGroups = new Map<string, typeof activeSessions>();
            for (const s of activeSessions) {
              const existing = sessionGroups.get(s.sessionId) || [];
              existing.push(s);
              sessionGroups.set(s.sessionId, existing);
            }
            
            return (
              <div className="space-y-6">
                {/* Stream toolbar: end call | fullscreen + settings */}
                <div className="flex items-center justify-between">
                  <button
                    onClick={() => {
                      for (const [sid, parts] of sessionGroups.entries()) {
                        const peer = node.getOrCreatePeer(parts[0].peerAddress);
                        peer.avStreamManager.endAudioStream(sid, 'hangup');
                      }
                    }}
                    className="grid size-9 place-items-center rounded-full bg-error text-on-error hover:bg-error/80 transition-colors"
                    title="End Call"
                  >
                    <PhoneOff size={16} />
                  </button>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => {
                        window.dispatchEvent(new CustomEvent("email-panel-close"));
                        window.dispatchEvent(new CustomEvent("calendar-panel-close"));
                        window.dispatchEvent(new CustomEvent("av-stream-panel-open"));
                      }}
                      className="grid size-9 place-items-center rounded-full bg-surface-container text-on-surface-variant hover:bg-surface-container-high transition-colors"
                      title="Open A/V Stream Panel"
                    >
                      <Fullscreen size={16} />
                    </button>
                    <AVSettingsDialog>
                      <mui.Dialog.Trigger
                        className="grid size-9 place-items-center rounded-full bg-surface-container text-on-surface-variant hover:bg-surface-container-high transition-colors"
                        title="A/V Settings"
                      >
                        <Settings size={16} />
                      </mui.Dialog.Trigger>
                    </AVSettingsDialog>
                  </div>
                </div>

                {Array.from(sessionGroups.entries()).map(([sessionId, participants]) => {
                  const firstParticipant = participants[0];
                  const isGroupCall = firstParticipant.session.isGroupCall;
                  
                  return (
                    <div key={sessionId} className="space-y-2">
                      {/* Group call indicator with moderator crown */}
                      {isGroupCall && (
                        <div className="text-label-sm text-tertiary font-medium px-2 flex items-center gap-1.5">
                          Group Call • {participants.length} participant{participants.length !== 1 ? 's' : ''}
                          {/* Show if current user is the moderator/initiator */}
                          {firstParticipant.session.initiatorAddress === node.session.address && (
                            <span className="inline-flex items-center gap-0.5 text-tertiary" title="You are the moderator">
                              <CrownIcon size={12} />
                            </span>
                          )}
                        </div>
                      )}
                      
                      {/* Remote peer windows - single column, scrollable for groups */}
                      <div className={isGroupCall ? "space-y-2 max-h-[60vh] overflow-y-auto" : "space-y-2"}>
                        {participants.map(({ peerAddress, contactName, contactInitials, contactProfile, session }) => (
                          <StreamWindow
                            key={peerAddress}
                            session={session}
                            peerAddress={peerAddress}
                            contactName={contactName}
                            contactInitials={contactInitials}
                            contactProfile={contactProfile}
                            isLocal={false}
                            isGroupInitiator={isGroupCall && session.initiatorAddress === peerAddress}
                          />
                        ))}
                      </div>
                      
                      {/* Local peer video window (bottom) */}
                      <StreamWindow
                        session={firstParticipant.session}
                        peerAddress={node.session.address}
                        contactName="You"
                        contactInitials={node.session.name.slice(0, 2).toUpperCase()}
                        contactProfile={{ avatarColor: userPreferences.avatarColor, avatarEmoji: userPreferences.avatarEmoji }}
                        isLocal={true}
                        isGroupInitiator={isGroupCall && firstParticipant.session.initiatorAddress === node.session.address}
                        onToggleVideo={async (enable: boolean) => {
                          // Toggle video for the first peer's AVStreamManager (they share the same session)
                          const peer = node.getOrCreatePeer(participants[0].peerAddress);
                          return peer.avStreamManager.toggleVideo(sessionId, enable);
                        }}
                        onToggleScreenShare={async (enable: boolean) => {
                          const peer = node.getOrCreatePeer(participants[0].peerAddress);
                          return peer.avStreamManager.toggleScreenShare(sessionId, enable);
                        }}
                        onEndCall={async () => {
                          // GROUP CALL END FIX: Call endAudioStream once via any participant's AVStreamManager.
                          // endAudioStream internally iterates session.peerAddresses and sends to all peers.
                          // For group calls, wireReason is auto-detected:
                          //   - Initiator → 'group_end' (ends call for everyone)
                          //   - Non-initiator → 'participant_leave' (only they leave)
                          const peer = node.getOrCreatePeer(participants[0].peerAddress);
                          await peer.avStreamManager.endAudioStream(sessionId, 'hangup');
                        }}
                      />
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </mui.Tabs.Panel>

        <mui.Tabs.Panel
          value="email"
          className="flex-1 basis-0 overflow-hidden"
        >
          <EmailListPanel 
            onSelectEmail={(email) => {
              if (email) {
                window.dispatchEvent(new CustomEvent("email-selected", { detail: email }));
              }
            }}
          />
        </mui.Tabs.Panel>
      </mui.Tabs.Root>
    </div>
    
    {/* New Group Dialog - triggered from empty groups tab */}
    {showNewGroupDialog && (
      <NewGroupChatDialog
        open={showNewGroupDialog}
        onOpenChange={(open: boolean) => {
          if (!open) setShowNewGroupDialog(false);
        }}
      >
        <div className="hidden" />
      </NewGroupChatDialog>
    )}
    </>
  );
}

export function ChatListItem({
  chat,
  connectionHealth,
  badgeCount,
  ...props
}: {
  chat: {
    address: string;
    name: ReactNode;
    initials: string;
    lastMessage: ReactNode;
    lastActive: ReactNode;
    profile?: any; // Remote peer profile for avatar/emoji
  };
  // Badge count for unsynced outbound + unread inbound messages
  badgeCount?: number;
  connectionHealth?: {
    state: RTCPeerConnectionState;
    signalingState: RTCSignalingState;
    iceState: RTCIceConnectionState;
    iceGatheringState: RTCIceGatheringState;
    lastUpdated?: number;
    // Health metrics
    rtt?: number;
    jitter?: number;
    packetsLost?: number;
    bytesSent?: number;
    bytesReceived?: number;
    healthScore?: "green" | "yellow" | "red" | "gray";
    lastStatsUpdate?: number;
  };
} & ComponentProps<"a">) {
  const db = useMobyDB();
  const [settingsOpen, setSettingsOpen] = useState(false);

  // Fetch ContactEntity for the peer settings dialog
  const contactEntity = useSuspenseLiveQuery(async () => {
    return await db.contacts.get({ address: chat.address });
  }, [chat.address]);
  
  return (
    <div className="group relative">
      <Link to={`/chats/${chat.address}`} asChild>
        <a
          className={`text-on-surface aria-selected:bg-secondary-container aria-selected:text-on-secondary-container group relative flex cursor-pointer items-center p-4 transition duration-150`}
          {...props}
        >
        <span
          aria-hidden
          className="group-hover:bg-on-surface-variant/8 group-active:bg-on-surface-variant/10 absolute inset-0 transition"
        />
        <div className="relative mr-4 size-9">
          <div 
            className="grid size-9 place-items-center overflow-hidden rounded-full text-white"
            style={{ backgroundColor: getPeerAvatarColor(chat.profile) }}
          >
            <div className="text-label-md">{chat.initials}</div>
            {renderPeerAvatarEmoji(chat.profile, "sm")}
          </div>
          
          {/* Connection health indicator — P0-C: presence-aware derived state.
              `connectionHealth` is intentionally undefined for group chats
              (see chat-list-panel.tsx:814); only render for direct chats. */}
          {connectionHealth && (
            <PeerHealthDot
              peerId={chat.address}
              connection={connectionHealth}
              size="md"
            />
          )}
        </div>
        <div className="mr-4 min-w-0 flex-1">
          <h3 className="text-body-lg truncate">{chat.name}</h3>
          <p className={`text-body-md text-on-secondary-container truncate`}>
            {chat.lastMessage}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Unsynced/unread message badge */}
          {badgeCount !== undefined && badgeCount > 0 && (
            <div 
              className="flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-primary text-on-primary text-label-sm font-medium"
              title={`${badgeCount} pending message${badgeCount > 1 ? 's' : ''}`}
            >
              {badgeCount > 99 ? '99+' : badgeCount}
            </div>
          )}
          <div className="text-on-surface-variant text-label-md">
            {chat.lastActive}
          </div>
        </div>
        </a>
      </Link>
      
      {/* Peer settings button */}
      {contactEntity && (
        <EditContactDialog
          contact={contactEntity}
          open={settingsOpen}
          onOpenChange={setSettingsOpen}
        >
          <button
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setSettingsOpen(true); }}
            className="absolute right-2 top-1/2 -translate-y-1/2 size-8 rounded-full bg-secondary-container text-on-secondary-container opacity-0 transition-opacity duration-150 hover:bg-secondary-container/80 group-hover:opacity-100 flex items-center justify-center"
            title="Peer settings"
            aria-label="Peer settings"
          >
            <Settings2 className="size-4" />
          </button>
        </EditContactDialog>
      )}
    </div>
  );
}
